import { tr } from '@/lib/i18n-core';
import { useState, type ReactNode } from 'react';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Textarea } from '@/components/ui/primitives';

export interface ApprovalFact {
  label: string;
  value: ReactNode;
}

export function ApprovalModal({
  title,
  description,
  facts,
  blockedReasons,
  canDecide,
  canApprove,
  busy,
  error,
  onApprove,
  onReject,
  onClose,
}: {
  title: string;
  description?: string;
  facts: ApprovalFact[];
  blockedReasons: string[];
  canDecide: boolean;
  canApprove: boolean;
  busy: boolean;
  error: string | null;
  onApprove: (notes: string) => void;
  onReject: (notes: string) => void;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState('');
  const rejectReady = notes.trim().length >= 3;

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'close')}
          </Button>
          {canApprove && (
            <Button variant="outline" onClick={() => onReject(notes.trim())} disabled={!rejectReady || busy} loading={busy}>
              {tr('billing', 'reject')}
            </Button>
          )}
          {canApprove && (
            <Button onClick={() => onApprove(notes.trim())} disabled={!canDecide || busy} loading={busy}>
              {tr('billing', 'approve')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{fact.label}</dt>
              <dd className="font-medium">{fact.value}</dd>
            </div>
          ))}
        </dl>

        {blockedReasons.length > 0 && (
          <Alert variant="warning" title={tr('billing', 'you_cannot_approve_this_yet')}>
            <ul className="mt-1 list-disc space-y-1 ps-5">
              {blockedReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </Alert>
        )}

        {canApprove ? (
          <Field label={tr('billing', 'note')} hint={tr('billing', 'required_to_reject_kept_with_the_record_and')}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={400} />
          </Field>
        ) : (
          <Alert>{tr('billing', 'your_role_can_ask_for_this_but_cannot_decide_it')}</Alert>
        )}

        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
